import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import contributorsData from './contributors.json';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import { db } from '../../config/firebase.js';
import { collection, getDocs, query, where } from 'firebase/firestore';
import logger from '../../utils/logger';
import gradesConfig from '../GradePage/grades.json';

function ContributorsPage() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // Live metrics fetched from Firestore (read-only)
  const [liveMetrics, setLiveMetrics] = useState([]);
  // Raw contributions to enable grade/subject filtering client-side
  const [rawContribs, setRawContribs] = useState([]);
  const [gradeFilter, setGradeFilter] = useState('All');
  const [subjectFilter, setSubjectFilter] = useState('All');

  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      try {
        const acc = new Map(); // key: normalized contributor name -> metrics
        const raw = []; // array of { name, grade, subject, ratingTotal, ratingAmount, reportAmount }

        // Helpers to mirror GradePage behavior
        const toPascalCase = (str = '') => str.replace(/(^\w|[\s-_]\w)/g, (m) => m.replace(/[\s-_]/, '').toUpperCase());
        const trimGradeSpace = (grade = '') => grade.trim().replace(/\s+/g, '');

        // Iterate over Grades -> Subjects collections
        const gradeItems = Array.isArray(gradesConfig) ? gradesConfig : [];
        // Collect all collection refs first to parallelize
        const tasks = [];
        for (const grade of gradeItems) {
          const gradeName = grade?.name || '';
          const gradeDocId = trimGradeSpace(gradeName);
          const subjects = Array.isArray(grade?.subjects) ? grade.subjects : [];

          for (const subjName of subjects) {
            const subjectId = toPascalCase(subjName);
            const colRef = collection(db, 'PDFS', gradeDocId, subjectId);
            tasks.push(
              // Verified resources only: the rules allow this signed out, and
              // unmoderated uploads should not count towards recognition
              getDocs(query(colRef, where('verified', '==', true))).then((snap) => {
                snap.forEach((docSnap) => {
                  const d = docSnap.data() || {};
                  // Try multiple fields for contributor name fallback
                  const primary = (d.contributor ?? d.uploader ?? d.uploadedBy ?? d.authorName ?? d.ownerName ?? '').toString();
                  const fallbackId = (d.userID ?? d.owner ?? d.createdBy ?? '').toString();
                  const rawName = (primary || fallbackId || '').trim();
                  const isAnon = !rawName || /anonymous/i.test(rawName);
                  if (isAnon) return; // Only non-anonymous

                  // Push raw entry for filtering later
                  raw.push({
                    name: rawName,
                    grade: gradeDocId,
                    subject: subjectId,
                    ratingTotal: Number(d.ratingTotal || 0),
                    ratingAmount: Number(d.ratingAmount || 0),
                    reportAmount: Number(d.reportAmount || 0),
                  });

                  // Aggregate
                  const prev = acc.get(rawName) || {
                    name: rawName,
                    contributionsCount: 0,
                    ratingTotal: 0,
                    ratingsCount: 0,
                    reportsCount: 0,
                  };
                  prev.contributionsCount += 1;
                  const rt = Number(d.ratingTotal || 0);
                  const ra = Number(d.ratingAmount || 0);
                  prev.ratingTotal += isFinite(rt) ? rt : 0;
                  prev.ratingsCount += isFinite(ra) ? ra : 0;
                  const rep = Number(d.reportAmount || 0);
                  prev.reportsCount += isFinite(rep) ? rep : 0;
                  acc.set(rawName, prev);
                });
              })
            );
          }
        }
        await Promise.all(tasks);

        // Finalize averages
        let results = Array.from(acc.values()).map((it) => ({
          name: it.name,
          contributionsCount: it.contributionsCount,
          averageRating: it.ratingsCount > 0 ? it.ratingTotal / it.ratingsCount : undefined,
          ratingsCount: it.ratingsCount,
          reportsCount: it.reportsCount,
        }));

        // Fallback: if no PDF metrics found, derive from analytics_data (file_upload events)
        if (results.length === 0) {
          const acc2 = new Map();
          const analyticsRef = collection(db, 'analytics_data');
          const analyticsSnap = await getDocs(analyticsRef);
          analyticsSnap.forEach((docSnap) => {
            const d = docSnap.data() || {};
            if (d.event !== 'file_upload') return;
            const primary = (d.contributor ?? d.uploader ?? d.uploadedBy ?? d.authorName ?? d.ownerName ?? '').toString();
            const emailName = (d.userID || '').toString().trim();
            const rawName = (primary || '').toString().trim();
            let name = rawName && !/anonymous/i.test(rawName) ? rawName : emailName;
            if (!name) return;
            const prev = acc2.get(name) || { name, contributionsCount: 0 };
            prev.contributionsCount += 1;
            acc2.set(name, prev);
          });
          results = Array.from(acc2.values());
        }

        if (isMounted) {
          setLiveMetrics(results);
          setRawContribs(raw);
        }
      } catch (err) {
        logger.error('Contributors metrics fetch error:', err);
        if (isMounted) {
          setLiveMetrics([]);
          setRawContribs([]);
        }
      }
    };

    fetchAll();
    return () => {
      isMounted = false;
    };
  }, []);

  // Page entrance + short confetti burst
  useEffect(() => {
    setMounted(true);
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 1400);
    return () => clearTimeout(t);
  }, []);

  // Build contributors list from raw contributions applying grade/subject filters
  const contributors = useMemo(() => {
    if (!Array.isArray(rawContribs) || rawContribs.length === 0) {
      // Fallback to aggregated if raw not available
      const items = (Array.isArray(liveMetrics) ? [...liveMetrics] : []).map((it) => ({
        ...it,
        // Reset reports to zero for display on contributors page
        reportsCount: 0,
      }));
      items.sort((a, b) => {
        const ac = a.contributionsCount || 0;
        const bc = b.contributionsCount || 0;
        if (bc !== ac) return bc - ac;
        const ar = a.averageRating || 0;
        const br = b.averageRating || 0;
        if (br !== ar) return br - ar;
        const arp = a.reportsCount ?? Number.MAX_SAFE_INTEGER;
        const brp = b.reportsCount ?? Number.MAX_SAFE_INTEGER;
        if (arp !== brp) return arp - brp;
        return (a.name || '').localeCompare(b.name || '');
      });
      return items;
    }

    const gf = (gradeFilter || 'All').trim();
    const sf = (subjectFilter || 'All').trim();
    const filtered = rawContribs.filter((c) => {
      const gradeOk = gf === 'All' ? true : c.grade === gf;
      const subjectOk = sf === 'All' ? true : c.subject === sf;
      return gradeOk && subjectOk;
    });

    const agg = new Map();
    for (const c of filtered) {
      const prev = agg.get(c.name) || { name: c.name, contributionsCount: 0, ratingTotal: 0, ratingsCount: 0, reportsCount: 0 };
      prev.contributionsCount += 1;
      prev.ratingTotal += isFinite(c.ratingTotal) ? c.ratingTotal : 0;
      prev.ratingsCount += isFinite(c.ratingAmount) ? c.ratingAmount : 0;
      prev.reportsCount += isFinite(c.reportAmount) ? c.reportAmount : 0;
      agg.set(c.name, prev);
    }
    const items = Array.from(agg.values()).map((it) => ({
      name: it.name,
      contributionsCount: it.contributionsCount,
      averageRating: it.ratingsCount > 0 ? it.ratingTotal / it.ratingsCount : undefined,
      ratingsCount: it.ratingsCount,
      // Reset reports to zero for display on contributors page
      reportsCount: 0,
    }));

    items.sort((a, b) => {
      const ac = a.contributionsCount || 0;
      const bc = b.contributionsCount || 0;
      if (bc !== ac) return bc - ac;
      const ar = a.averageRating || 0;
      const br = b.averageRating || 0;
      if (br !== ar) return br - ar;
      const arp = a.reportsCount ?? Number.MAX_SAFE_INTEGER;
      const brp = b.reportsCount ?? Number.MAX_SAFE_INTEGER;
      if (arp !== brp) return arp - brp;
      return (a.name || '').localeCompare(b.name || '');
    });
    return items;
  }, [rawContribs, liveMetrics, gradeFilter, subjectFilter]);

  const founders = contributorsData.founders || [];
  const admin = contributorsData.administrative_assistance || [];

  const leaderboard = useMemo(() => {
    // Show only if we have actual contribution metrics
    const withCounts = contributors.filter((c) => typeof c.contributionsCount === 'number' && c.contributionsCount > 0);
    return withCounts.slice(0, 5);
  }, [contributors]);

  const getBadges = (c) => {
    const badges = [];
    const cc = c.contributionsCount || 0;
    const ar = c.averageRating || 0;
    const rc = typeof c.reportsCount === 'number' ? c.reportsCount : undefined;
    const rcount = c.ratingsCount || 0;

    // Base badge
    badges.push({ label: 'Contributor', color: 'bg-blue-100 text-blue-700', emoji: '🏅', title: 'Shared resources with the community' });

    // Contributions badges
    if (cc >= 10) {
      badges.push({ label: 'Power Uploader', color: 'bg-yellow-100 text-yellow-800', emoji: '⚡', title: '10+ resources uploaded' });
    } else if (cc >= 5) {
      badges.push({ label: 'Active Contributor', color: 'bg-green-100 text-green-800', emoji: '🚀', title: '5+ resources uploaded' });
    } else if (cc >= 1) {
      badges.push({ label: 'Rising Star', color: 'bg-purple-100 text-purple-800', emoji: '🌟', title: 'Getting started with contributions' });
    }

    // Rating badges
    if (ar >= 4.5 && rcount >= 5) {
      badges.push({ label: 'Top Rated', color: 'bg-pink-100 text-pink-800', emoji: '⭐', title: 'Avg rating ≥ 4.5 with 5+ ratings' });
    } else if (ar >= 4) {
      badges.push({ label: 'Well Rated', color: 'bg-indigo-100 text-indigo-800', emoji: '💯', title: 'Avg rating ≥ 4' });
    }

    // Quality/Reports badges
    if (rc === 0 && cc >= 3) {
      badges.push({ label: 'Quality Guardian', color: 'bg-emerald-100 text-emerald-800', emoji: '🛡️', title: '0 reports across multiple uploads' });
    } else if (rc !== undefined && rc <= 1 && cc >= 1) {
      badges.push({ label: 'Low Reports', color: 'bg-teal-100 text-teal-800', emoji: '✅', title: 'Very few reported resources' });
    }

    return badges;
  };

  // Search + pagination for All contributors
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    // Reset to first page when search changes
    setPage(1);
  }, [searchTerm]);

  const filteredContributors = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return contributors;
    return contributors.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [contributors, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredContributors.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageContributors = filteredContributors.slice(startIdx, endIdx);

  return (
    <>
      <GoogleAnalytics page="contributorsPage" eventType="Contributors_page" />
      <Helmet>
        <title>Contributors - Share2Teach</title>
        <meta
          name="description"
          content="Meet the dedicated founders, administrative staff, and resource contributors behind Share2Teach."
        />
        <meta
          name="keywords"
          content="Share2Teach contributors, founders, administrative assistance, resource contributors, open educational resources, nwu, nwu enterprises, nwu education, north west university"
        />
        <meta property="og:title" content="Contributors - Share2Teach" />
        <meta
          property="og:description"
          content="Meet the dedicated founders, administrative staff, and resource contributors behind Share2Teach."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="px-5 pt-8 max-w-6xl mx-auto">
        {/* Page header */}
  <div className={`text-center mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold mb-3">
            🎉 Bragging Rights Platform
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Contributors</span>
          </h1>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">Shoutouts to the stars sharing knowledge. Non-anonymous contributors are highlighted below.</p>
          <div className="mt-4 h-1 w-24 mx-auto bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />
        </div>

        {/* Founders */}
        {founders.length > 0 && (
          <section className={`mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">👑</span>
              <h2 className="text-2xl font-semibold text-gray-800">Founders</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {founders.map((f, i) => (
                <div key={i} className={`bg-white rounded-xl shadow p-4 flex items-center gap-3 border border-blue-50 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                     style={{ transitionDelay: `${100 + i * 60}ms` }}>
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">{f.charAt(0)}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{f}</div>
                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" title="Founder">
                      <span>👑</span> Founder
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Administrative assistance */}
        {admin.length > 0 && (
          <section className={`mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🛠️</span>
              <h2 className="text-2xl font-semibold text-gray-800">Administrative assistance</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {admin.map((a, i) => (
                <div key={i} className={`bg-white rounded-xl shadow p-4 flex items-center gap-3 border border-gray-100 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                     style={{ transitionDelay: `${100 + i * 60}ms` }}>
                  <div className="h-10 w-10 rounded-full bg-gray-800 text-white flex items-center justify-center font-bold">{a.charAt(0)}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{a}</div>
                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" title="Administrative assistance">
                      <span>🛠️</span> Team
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <section className="mb-6">
          <div className="bg-white rounded-xl shadow p-4 border border-gray-100 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Grade</label>
              <select
                value={gradeFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setGradeFilter(v);
                  setSubjectFilter('All');
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-300 outline-none"
              >
                <option value="All">All</option>
                {Array.isArray(gradesConfig) && gradesConfig.map((g) => (
                  <option key={g.name} value={g.name.replace(/\s+/g, '')}>{g.name.replace(/\s+/g, '')}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-300 outline-none"
              >
                <option value="All">All</option>
                {(() => {
                  // Determine subject options from selected grade
                  const selectedGrade = (gradesConfig || []).find((g) => g.name.replace(/\s+/g, '') === gradeFilter);
                  const subjects = selectedGrade?.subjects || [];
                  // mirror GradePage subject id transform displayed as PascalCase id
                  const toPascalCase = (str = '') => str.replace(/(^\w|[\s-_]\w)/g, (m) => m.replace(/[\s-_]/, '').toUpperCase());
                  return subjects.map((s) => {
                    const sid = toPascalCase(s);
                    return (
                      <option key={sid} value={sid}>{sid}</option>
                    );
                  });
                })()}
              </select>
            </div>
          </div>
        </section>

        {/* Hall of Fame + Leaderboard (only if metrics exist) */}
        {leaderboard.length > 0 ? (
          <section className="mb-12 relative">
            {/* Lightweight confetti burst */}
            {showConfetti && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                {[...Array(24)].map((_, i) => (
                  <span
                    key={i}
                    className={`absolute block h-2 w-2 rounded-sm ${i % 3 === 0 ? 'bg-pink-400' : i % 3 === 1 ? 'bg-yellow-400' : 'bg-blue-400'}`}
                    style={{
                      top: `${10 + (i * 3) % 70}%`,
                      left: `${(i * 41) % 100}%`,
                      transform: `translateY(${mounted ? 0 : -20}px) rotate(${(i * 57) % 360}deg)`,
                      opacity: mounted ? 0.9 : 0,
                      transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
                    }}
                  />
                ))}
              </div>
            )}
            {/* Hall of Fame - Top 3 */}
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">Hall of Fame</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {leaderboard.slice(0, 3).map((c, idx) => {
                  const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉';
                  const ring = idx === 0 ? 'ring-4 ring-amber-400' : idx === 1 ? 'ring-4 ring-gray-400' : 'ring-4 ring-orange-700';
                  return (
                    <div key={c.name + 'hof' + idx} className="bg-white rounded-xl shadow p-5 flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold ${ring}`}>
                        {c.name?.charAt(0) || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{medal}</span>
                          <h3 className="text-base font-semibold text-gray-900 truncate">{c.name}</h3>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 flex gap-4">
                          <span>Contributions: <span className="font-medium text-gray-800">{c.contributionsCount}</span></span>
                          {typeof c.averageRating === 'number' && (
                            <span>Avg rating: <span className="font-medium text-gray-800">{c.averageRating.toFixed(1)}</span></span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Leaderboard table */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Top contributors</h2>
              <span className="text-sm text-gray-500">Based on number of resources</span>
            </div>
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-card">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contributor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contributions</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {leaderboard.map((c, idx) => {
                    const rowMedal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
                    return (
                      <tr key={c.name + idx} className={`hover:bg-gray-50 ${idx === 0 ? 'bg-amber-50' : idx === 1 ? 'bg-gray-50' : idx === 2 ? 'bg-orange-50' : ''}`}>
                        <td className="px-4 py-3 font-semibold text-gray-700">{rowMedal || idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-1">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white text-sm font-bold">
                                {c.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{c.name}</div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {getBadges(c).map((b, i) => (
                                  <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.color}`} title={b.title}>
                                    <span>{b.emoji}</span>
                                    {b.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{c.contributionsCount ?? '—'}</td>
                        <td className="px-4 py-3">{typeof c.averageRating === 'number' ? c.averageRating.toFixed(1) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Badges legend */}
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-gray-700 font-medium mr-1">Badges:</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><span>🏅</span>Contributor</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><span>⚡</span>Power Uploader</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><span>🚀</span>Active Contributor</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"><span>🌟</span>Rising Star</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800"><span>⭐</span>Top Rated</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"><span>💯</span>Well Rated</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"><span>🛡️</span>Quality Guardian</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800"><span>✅</span>Low Reports</span>
              </div>
            </div>
          </section>
        ) : (
          // Friendly note if no metrics for rankings
          <section className="mb-12">
            <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-lg p-4">
              <div className="font-semibold mb-1">Rankings coming alive</div>
              <p className="text-sm">Once contribution metrics are available (uploads, ratings, and reports), we’ll show a live Top Contributors leaderboard here.</p>
            </div>
          </section>
        )}

        {/* All contributors grid (non-anonymous only) */}
        <section className="mb-16">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-2xl font-semibold text-gray-800">All contributors</h2>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search contributors..."
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-300 outline-none"
              />
            </div>
          </div>
          {filteredContributors.length === 0 ? (
            <div className="text-gray-500">No contributors available.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pageContributors.map((c, idx) => {
                const badges = getBadges(c);
                const hasMetrics =
                  typeof c.contributionsCount === 'number' || typeof c.averageRating === 'number' || typeof c.reportsCount === 'number';
                return (
                  <div key={c.name + idx} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                          {c.name?.charAt(0) || '?'}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-semibold text-gray-900 truncate">{c.name}</h3>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {badges.map((b, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.color}`} title={b.title}>
                              <span>{b.emoji}</span>
                              {b.label}
                            </span>
                          ))}
                        </div>
                        {hasMetrics && (
                          <div className="mt-3 text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                            {typeof c.contributionsCount === 'number' && (
                              <span>
                                <span className="font-medium text-gray-800">Contributions:</span> {c.contributionsCount}
                              </span>
                            )}
                            {typeof c.averageRating === 'number' && (
                              <span>
                                <span className="font-medium text-gray-800">Avg rating:</span> {c.averageRating.toFixed(1)}
                                {c.ratingsCount ? ` (${c.ratingsCount})` : ''}
                              </span>
                            )}
                            {typeof c.reportsCount === 'number' && (
                              <span>
                                <span className="font-medium text-gray-800">Reports:</span> {c.reportsCount}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination controls */}
          {filteredContributors.length > 0 && (
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-gray-600">
                Showing {startIdx + 1}-{Math.min(endIdx, filteredContributors.length)} of {filteredContributors.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded border text-sm font-medium ${currentPage === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 rounded border text-sm font-medium ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Community shoutouts - spotlight top 5 */}
        {leaderboard.length > 0 && (
          <section className="mb-14">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">📣</span>
              <h2 className="text-2xl font-semibold text-gray-800">Community shoutouts</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {leaderboard.map((c, idx) => {
                const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🏅';
                return (
                  <div key={c.name + 'shout' + idx} className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5 shadow-lg">
                    <div className="relative z-10 flex items-start gap-4">
                      <div className="h-12 w-12 rounded-full bg-white text-blue-700 flex items-center justify-center font-bold text-lg">
                        {c.name?.charAt(0) || '?'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xl" title="Rank medal">{medal}</span>
                          <h3 className="text-lg font-semibold truncate">{c.name}</h3>
                        </div>
                        <p className="mt-1 text-blue-100 text-sm">
                          {`Shared ${c.contributionsCount || 0} resources${typeof c.averageRating === 'number' ? ` · Avg ${c.averageRating.toFixed(1)}★` : ''}`}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {getBadges(c).map((b, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white border border-white/20" title={b.title}>
                              <span>{b.emoji}</span>
                              {b.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Decorative blobs */}
                    <div className="absolute -top-8 -right-8 h-40 w-40 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute -bottom-10 -left-10 h-48 w-48 bg-white/10 rounded-full blur-3xl" />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* CTA: Encourage contributions */}
        <section className="mb-20">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 shadow">
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold">Become a contributor</h2>
              <p className="mt-2 text-blue-100 max-w-2xl">
                Share your lesson plans, activities, and resources with the community. Earn badges, climb the leaderboard, and make a real impact.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/contribute')}
                  className="inline-flex items-center gap-2 bg-white text-blue-700 font-semibold px-4 py-2 rounded shadow hover:shadow-md hover:bg-blue-50 transition"
                >
                  Start contributing
                  <span>→</span>
                </button>
                <a
                  href="/self-directed-learning"
                  className="inline-flex items-center gap-2 bg-transparent border border-white/40 text-white px-4 py-2 rounded hover:bg-white/10 transition"
                >
                  Explore resources
                </a>
              </div>
            </div>
            {/* Decorative blobs */}
            <div className="absolute -top-8 -right-8 h-40 w-40 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-10 -left-10 h-48 w-48 bg-white/10 rounded-full blur-3xl" />
          </div>
        </section>
      </div>
    </>
  );
}

export default ContributorsPage;
